import { Link } from "react-router-dom";
import { X, Gift, PenTool } from "lucide-react";
import { Sparkle, Heart, Star } from "../doodles/Doodles";

/**
 * WelcomeSweetener — modal shown once, right after a new user signs up.
 * Communicates the FREE handwritten note perk for the first order.
 *
 * Props:
 *   userName — for a warm greeting
 *   onClose  — parent handler
 */
export default function WelcomeSweetener({ userName = "", onClose }) {
  return (
    <div
      className="fixed inset-0 z-[120] bg-[#2D2424]/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="welcome-sweetener-modal"
    >
      <div
        className="relative w-full max-w-md bg-white border-2 border-[#2D2424] rounded-3xl overflow-hidden -rotate-1"
        style={{ boxShadow: "8px 8px 0 #7A1F2B" }}
      >
        <Sparkle color="#FFD93D" className="absolute top-3 right-6 w-6 animate-twinkle pointer-events-none"/>
        <Heart color="#FF6B9D" className="absolute bottom-6 left-4 w-5 animate-bounce-doodle pointer-events-none"/>
        <Star color="#BAE1FF" className="absolute top-8 left-8 w-5 animate-spin-slow pointer-events-none"/>

        <button
          type="button"
          onClick={onClose}
          data-testid="welcome-close"
          aria-label="close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[#2D2424] flex items-center justify-center hover:bg-[#FFE7F1] z-10"
          style={{ boxShadow: "2px 2px 0 #2D2424" }}
        >
          <X className="w-4 h-4"/>
        </button>

        <div
          className="px-6 pt-8 pb-6 text-center"
          style={{ background: "linear-gradient(135deg, #FFE7F1 0%, #FFF7DC 100%)" }}
        >
          <div
            className="mx-auto w-16 h-16 bg-white border-2 border-[#2D2424] rounded-full flex items-center justify-center rotate-3"
            style={{ boxShadow: "3px 3px 0 #7A1F2B" }}
          >
            <Gift className="w-8 h-8 text-[#7A1F2B]" strokeWidth={2}/>
          </div>
          <h2 className="font-doodle text-3xl sm:text-4xl text-[#7A1F2B] mt-3 leading-tight">
            Welcome{userName ? `, ${userName.split(" ")[0]}` : ""}! 🎁
          </h2>
          <p className="font-hand text-sm sm:text-base text-[#5C5555] mt-2 italic">
            A little something on us for your very first order.
          </p>
        </div>

        <div className="px-6 py-6">
          <div
            className="border-2 border-dashed border-[#FF6B9D] rounded-2xl p-5 bg-[#FFE7F1] text-center"
            data-testid="welcome-perk-note"
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-[#FF6B9D] text-white flex items-center justify-center">
              <PenTool className="w-5 h-5"/>
            </div>
            <p className="font-doodle text-xl sm:text-2xl text-[#2D2424] mt-3 leading-tight">
              FREE handwritten note
            </p>
            <p className="font-hand text-sm text-[#5C5555] mt-2 leading-relaxed">
              We&apos;ll hand-letter a message on your hamper — on the house, for your very first order.
              Just add your note at product page &amp; we&apos;ll take care of the rest.
            </p>
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Link
            to="/shop/her"
            onClick={onClose}
            data-testid="welcome-start-shopping"
            className="btn-primary flex-1"
          >
            <Gift className="w-4 h-4"/> Start shopping
          </Link>
          <button
            type="button"
            onClick={onClose}
            data-testid="welcome-later"
            className="btn-secondary flex-1"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
